import { Component, OnInit, OnDestroy, HostListener, Input, computed, effect } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Subscription, interval } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CoreService } from '../../../services/core/core.service';
import { DelegationService, DelegationInfo } from '../../../core/auth/delegation.service';
import { SessionContextService } from '../../../core/auth/session-context.service';
import { SidebarService } from '../sidebar/sidebar.service';
import { AppSettings } from '../../../services/settings/app-settings.service';
import { OrganizationContextService } from '../../../services/core/organization-context.service';

@Component({
  standalone: false,
  selector: 'app-module-header',
  templateUrl: './module-header.component.html',
  styleUrl: './module-header.component.scss',
})
export class ModuleHeaderComponent implements OnInit, OnDestroy {
  /** URL of the module logo image. Falls back to Metabridge logo when showHomeButton=true. */
  @Input() logoUrl: string = '';

  /** Alt text for the logo image. */
  @Input() logoAlt: string = 'Logo';

  /** Menu items for the gear/settings popup (shown only when non-empty). */
  @Input() menuItems: MenuItem[] = [];

  /** When true, clicking the logo navigates to /home. Use this for the global (app-level) header. */
  @Input() showHomeButton: boolean = false;

  /** Resolved logo URL: explicit logoUrl, or Metabridge default when showHomeButton=true. */
  get effectiveLogoUrl(): string {
    if (this.logoUrl) return this.logoUrl;
    if (this.showHomeButton) {
      const base = (environment as any).assets?.s3BaseUrl || 'https://metabridge-assets.s3.us-east-1.amazonaws.com';
      return `${base}/logos/METABRIDGE-01.webp`;
    }
    return '';
  }

  /**
   * Muestra la marca de texto "METABRIDGE" (idéntica a mb-web-admin) en el header global de la app
   * (home). Dentro de un producto se mantiene el logo del producto (logoUrl).
   */
  get showBrandText(): boolean {
    return this.showHomeButton && !this.logoUrl;
  }

  public orgName: string = '';
  private orgNameRequestId = 0;

  // Propiedades para sesion delegada
  delegationInfo: DelegationInfo | null = null;
  isSupportUx: boolean = false;
  timeRemainingFormatted: string = '';
  showSupportPopover: boolean = false;
  private delegationSubscription?: Subscription;
  private timerSubscription?: Subscription;

  constructor(
    private router: Router,
    private coreService: CoreService,
    private delegationService: DelegationService,
    private sessionContext: SessionContextService,
    private sidebarService: SidebarService,
    private appSettings: AppSettings,
    private orgContext: OrganizationContextService,
  ) {
    effect(() => {
      const orgId = this.orgContext.getCurrentOrganization() || this.coreService.resolveCurrentOrganizationId();
      void this.updateOrganizationName(orgId);
    });
  }

  /** Oculta el toolbar en móvil cuando hay un panel de contenido abierto (ej. sidebar de dispositivo). */
  readonly contentPanelOpen = computed(() => this.appSettings.appContentPanelOpen());

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  changePage(url: string): void {
    this.router.navigate([url]);
  }

  redirectHome(): void {
    this.router.navigate(['home']);
  }

  async ngOnInit(): Promise<void> {
    // Forzar carga de delegación si ya existe en storage
    if (!this.delegationInfo) {
      const info = this.delegationService.getDelegationInfo();
      if (info) {
        this.delegationInfo = info;
        this.updateTimeRemaining();
        this.startTimeRemainingTimer();
      }
    }

    // Suscribirse a cambios en la sesion delegada
    this.initDelegationSubscription();

    await this.syncResolvedOrganizationName();
  }

  ngOnDestroy(): void {
    this.delegationSubscription?.unsubscribe();
    this.timerSubscription?.unsubscribe();
  }

  // ── Delegation ──────────────────────────────────────────────

  private initDelegationSubscription(): void {
    this.delegationSubscription = this.sessionContext.context$.subscribe((context) => {
      this.isSupportUx = context.isSupportUx;
      const info = this.sessionContext.getDelegationInfo();
      this.delegationInfo = info;
      if (info) {
        this.updateTimeRemaining();
        this.startTimeRemainingTimer();
      } else {
        this.timerSubscription?.unsubscribe();
        this.timeRemainingFormatted = '';
      }
    });
  }

  private startTimeRemainingTimer(): void {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = interval(1000).subscribe(() => {
      this.updateTimeRemaining();
    });
  }

  private updateTimeRemaining(): void {
    const info = this.delegationService.getDelegationInfo();
    if (info && info.timeRemaining > 0) {
      const hours = Math.floor(info.timeRemaining / 3600);
      const minutes = Math.floor((info.timeRemaining % 3600) / 60);
      const seconds = info.timeRemaining % 60;
      this.timeRemainingFormatted = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      this.timeRemainingFormatted = this.isSupportUx ? 'Activa' : 'Expirado';
    }
  }

  returnToAdminWeb(): void {
    const adminWebUrl = (environment as any).adminWebUrl || 'http://localhost:4300';
    window.location.href = adminWebUrl;
  }

  get isDelegatedOrgRestricted(): boolean {
    return this.isSupportUx && this.orgContext.isOrgRestricted();
  }

  get orgRestrictionLabel(): string {
    if (this.orgContext.isOrgInactive()) {
      return 'inactiva';
    }

    if (this.orgContext.isOrgDisabled()) {
      return 'inhabilitada';
    }

    return 'restringida';
  }

  get delegatedStatusText(): string {
    return this.isDelegatedOrgRestricted ? `Organización ${this.orgRestrictionLabel}` : 'Organización activa';
  }

  toggleSupportPopover(): void {
    this.showSupportPopover = !this.showSupportPopover;
  }

  closeSupportPopover(): void {
    this.showSupportPopover = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const barElement = target.closest('.delegated-session-bar');
    if (!barElement && this.showSupportPopover) {
      this.showSupportPopover = false;
    }
  }

  // ── Org name resolution ─────────────────────────────────────

  private async syncResolvedOrganizationName(): Promise<void> {
    try {
      const orgId = this.coreService.resolveCurrentOrganizationId();
      await this.updateOrganizationName(orgId);
    } catch (error) {
      console.error('[ModuleHeader] Error en ngOnInit:', error);
    }
  }

  private async updateOrganizationName(orgId: string | null): Promise<void> {
    const requestId = ++this.orgNameRequestId;
    if (!orgId || orgId === 'undefined') {
      this.orgName = '';
      return;
    }

    try {
      const name = await this.coreService.resolveOrganizationName(orgId);
      if (requestId === this.orgNameRequestId) {
        this.orgName = name;
      }
    } catch (error) {
      if (requestId === this.orgNameRequestId) {
        this.orgName = '';
      }
      console.error('[ModuleHeader] Error resolviendo nombre de organización:', error);
    }
  }
}
